import React, { FunctionComponent, useContext, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Paper,
  Slide,
  Snackbar,
  SwipeableDrawer,
  Tab,
  Tooltip,
  Typography
} from "@material-ui/core";
import useStyles from "./styles";
import { AddShoppingCart, Sync } from "@material-ui/icons";
import DeleteIcon from "@material-ui/icons/Delete";
import CloseIcon from "@material-ui/icons/Close";
import { useTheme } from "@material-ui/core/styles";
import { ContentCopy, Fastfood } from "@mui/icons-material";
import MenuIcon from "@material-ui/icons/Menu";
import Divider from "@material-ui/core/Divider";
import * as lab from "@material-ui/lab";
import { TabList } from "@material-ui/lab";
import ErrorIcon from "@material-ui/icons/Error";
import { useI18n } from "../../hooks/useI18n";
import { UserContext } from "../../UserProvider";
import { isProductCampaignReadOnly } from "../../models/User";

interface ProductHeaderProps {
  storeProduct: boolean;
  product: any;
  setShowEdit: (show: boolean) => void;
  setShowDelete: (show: boolean) => void;
  setShowSyncPriceTag?: () => void;
  setShowReportWaste?: (show: boolean) => void;
  setSelectedTab?: (event: React.ChangeEvent<{}>, newValue: string) => void;
}

export const ProductHeader: FunctionComponent<ProductHeaderProps> = ({
  storeProduct = false,
  product,
  setShowEdit,
  setShowDelete,
  setShowSyncPriceTag,
  setShowReportWaste,
  setSelectedTab
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useI18n();
  const user = useContext(UserContext);
  const readOnly = isProductCampaignReadOnly(user);

  const [copyToClipboardNotification, setCopyToClipboardNotification] =
    useState({ open: false, msg: "" });

  // Drawer for user actions menu
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const toggleActionsDrawer = () => {
    setActionsOpen(!actionsOpen);
  };

  const handleActionsOpen = () => {
    setActionsOpen(true);
  };

  const handleActionsClose = () => {
    setActionsOpen(false);
  };

  return (
    <>
      <Card>
        <CardContent style={{ padding: 0 }}>
          <Box display={"flex"} style={{ padding: 14 }}>
            <Box display={"flex"} alignItems={"center"} flexGrow={1}>
              <Avatar
                style={{
                  marginRight: theme.spacing(2),
                  padding: theme.spacing(3)
                }}
              >
                <Fastfood fontSize={"large"} style={{ marginTop: -5 }} />
              </Avatar>
              <Typography component={"span"}>
                <Typography variant={"subtitle1"} style={{ lineHeight: 1.1 }}>
                  {product.name}
                  <Tooltip title={t("common.productHeader.copyProductLink")}>
                    <IconButton
                      edge="start"
                      size={"small"}
                      style={{ marginLeft: "0.3rem" }}
                      color="inherit"
                      onClick={e => {
                        navigator.clipboard
                          .writeText(
                            `${location.protocol}//${location.host}/tenant-products/${product.id}`
                          )
                          .then(_ =>
                            setCopyToClipboardNotification({
                              open: true,
                              msg: t(
                                "common.productHeader.linkToProductCopiedToClipboard"
                              )
                            })
                          );
                      }}
                      aria-label={t("common.tooltip.copy")}
                    >
                      <ContentCopy style={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                </Typography>

                <Typography variant={"body2"} color={"textSecondary"}>
                  {product.sizing.value} {product.sizing.unit}{" "}
                  {product.brandName}
                </Typography>
              </Typography>
            </Box>
            <Box position={"relative"} display={"flex"} justifyContent={"end"}>
              {!readOnly && (
                <>
                  <Tooltip title={t("common.actions.editProduct")}>
                    <IconButton onClick={toggleActionsDrawer} color={"primary"}>
                      <MenuIcon />
                    </IconButton>
                  </Tooltip>
                  <Paper>
                    <SwipeableDrawer
                      anchor={"right"}
                      open={actionsOpen}
                      PaperProps={{
                        style: { position: "fixed", top: "inherit" }
                      }}
                      onClose={() => handleActionsClose()}
                      onOpen={() => handleActionsOpen()}
                    >
                      <Box className={classes.drawerAction}>
                        <Tooltip title={t("common.actions.close")}>
                          <IconButton onClick={() => handleActionsClose()}>
                            <CloseIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Divider variant={"fullWidth"} />
                      <Box className={classes.drawerMenuCategory}>
                        <Typography variant={"body1"} color={"textSecondary"}>
                          {t("common.productHeader.manageProduct")}
                        </Typography>
                      </Box>
                      <MenuItem
                        onClick={() => {
                          setShowEdit(true);
                          handleActionsClose();
                        }}
                      >
                        <Box className={classes.drawerAction}>
                          <IconButton>
                            <AddShoppingCart />
                          </IconButton>
                          <Typography variant={"body2"} component={"div"}>
                            {t("common.actions.editProduct")}
                          </Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setShowDelete(true);
                          handleActionsClose();
                        }}
                      >
                        <Box className={classes.drawerAction}>
                          <Tooltip
                            title={
                              storeProduct
                                ? t("common.productHeader.deactivateProduct")
                                : t("common.productHeader.deleteProduct")
                            }
                          >
                            <>
                              <IconButton>
                                <DeleteIcon />
                              </IconButton>
                              <Typography variant={"body2"} component={"div"}>
                                {storeProduct
                                  ? t("common.productHeader.deactivateProduct")
                                  : t("common.productHeader.deleteProduct")}
                              </Typography>
                            </>
                          </Tooltip>
                        </Box>
                      </MenuItem>
                      {setShowSyncPriceTag && (
                        <MenuItem
                          onClick={() => {
                            setShowSyncPriceTag();
                            handleActionsClose();
                          }}
                        >
                          <Box className={classes.drawerAction}>
                            <Tooltip
                              title={t("common.productHeader.syncPriceTag")}
                            >
                              <>
                                <IconButton>
                                  <Sync />
                                </IconButton>
                                <Typography variant={"body2"} component={"div"}>
                                  {t("common.productHeader.syncPriceTag")}
                                </Typography>
                              </>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      )}
                      {setShowReportWaste && (
                        <MenuItem
                          onClick={() => {
                            setShowReportWaste(true);
                            handleActionsClose();
                          }}
                        >
                          <Box className={classes.drawerAction}>
                            <Tooltip title={t("common.actions.reportWaste")}>
                              <>
                                <IconButton>
                                  <ErrorIcon />
                                </IconButton>
                                <Typography variant={"body2"} component={"div"}>
                                  {t("common.actions.reportWaste")}
                                </Typography>
                              </>
                            </Tooltip>
                          </Box>
                        </MenuItem>
                      )}
                    </SwipeableDrawer>
                  </Paper>
                </>
              )}
            </Box>
          </Box>
          {setSelectedTab !== undefined && (
            <>
              <Divider color={theme.palette.primary.dark} />
              <TabList
                onChange={setSelectedTab}
                variant={"scrollable"}
                scrollButtons={"on"}
                style={{ backgroundColor: "white" }}
              >
                <Tab label={t("common.productHeader.product")} value={"1"} />
                <Tab label={t("common.productHeader.deliveries")} value={"2"} />
                <Tab
                  label={t("common.productHeader.stocktaking")}
                  value={"3"}
                />
                <Tab label={t("common.productHeader.spoilage")} value={"4"} />
              </TabList>
            </>
          )}
        </CardContent>
      </Card>
      <Snackbar
        open={copyToClipboardNotification.open}
        autoHideDuration={1000}
        onClose={() => setCopyToClipboardNotification({ open: false, msg: "" })}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        TransitionComponent={props => <Slide {...props} direction="down" />}
      >
        <lab.Alert severity="success" style={{ width: "100%" }}>
          {copyToClipboardNotification.msg}
        </lab.Alert>
      </Snackbar>
    </>
  );
};
